import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { MaskLine } from "./Reveal";
import { ArrowDown } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { EditableText } from "@/components/site/edit/EditableText";
import { scrollToSelector } from "@/hooks/useLenis";
import { api, resolveImg } from "@/lib/api";

const NOISE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E";
const DEFAULT_HERO = "https://images.pexels.com/photos/16309117/pexels-photo-16309117.jpeg?auto=compress&cs=tinysrgb&w=1400";

function Ambient() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      <div className="absolute -top-[12%] -left-[8%] w-[45vw] h-[45vw] rounded-full bg-[#BE923C]/12 blur-[130px]" />
      <div className="absolute top-[15%] -right-[15%] w-[50vw] h-[50vw] rounded-full bg-[#003063]/8 blur-[120px]" />
      <div className="absolute -bottom-[18%] left-[18%] w-[38vw] h-[38vw] rounded-full bg-[#C4964C]/10 blur-[120px]" />
      <div className="absolute inset-0 opacity-[0.04] mix-blend-overlay" style={{ backgroundImage: `url("${NOISE}")` }} />
    </div>
  );
}

function ScrollCue({ t }) {
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }}
      className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-[#475569]"
    >
      <span className="text-xs uppercase tracking-widest">{t("hero.scroll")}</span>
      <ArrowDown size={16} className="animate-bounce" />
    </motion.div>
  );
}

function Copy({ t, centered = false }) {
  const navigate = useNavigate();
  return (
    <>
      <MaskLine delay={0.15}>
        <span className={`inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-[#BE923C] ${centered ? "justify-center" : ""}`}>
          <span className="w-8 h-px bg-[#BE923C]" /> <EditableText k="hero.eyebrow" as="span" />
        </span>
      </MaskLine>
      <h1 className={`mt-6 font-serif text-5xl sm:text-6xl lg:text-[5rem] leading-[0.95] tracking-tight text-[#0F172A] ${centered ? "mx-auto max-w-4xl" : ""}`}>
        <MaskLine delay={0.25}>{t("hero.title1")}</MaskLine>
        <MaskLine delay={0.35}>
          {t("hero.title2a")}{" "}
          <span className="italic bg-gradient-to-r from-[#BE923C] via-[#D8A94E] to-[#C4964C] bg-clip-text text-transparent">{t("hero.title2b")}</span>
        </MaskLine>
        <MaskLine delay={0.45}>{t("hero.title3")}</MaskLine>
      </h1>
      <motion.p
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.8 }}
        className={`mt-8 text-base sm:text-lg text-[#475569] leading-relaxed ${centered ? "mx-auto max-w-2xl" : "max-w-xl"}`}
      >
        <EditableText k="hero.subtitle" as="span" multiline />
      </motion.p>
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.8 }}
        className={`mt-10 flex flex-wrap items-center gap-4 ${centered ? "justify-center" : ""}`}
      >
        <button
          data-testid="hero-about-btn"
          onClick={() => navigate("/about")}
          className="rounded-full bg-gradient-to-r from-[#BE923C] to-[#C4964C] text-white text-sm font-semibold px-8 py-4 shadow-[0_10px_30px_-8px_rgba(190,146,60,0.5)] hover:shadow-[0_16px_38px_-6px_rgba(190,146,60,0.6)] hover:-translate-y-0.5 transition-[transform,box-shadow] duration-300"
        >
          <EditableText k="hero.about" as="span" />
        </button>
        <button
          data-testid="hero-programs-btn"
          onClick={() => scrollToSelector("#programs")}
          className="rounded-full bg-white/60 backdrop-blur-md border border-[#0F172A]/12 text-sm font-semibold px-8 py-4 hover:bg-white hover:border-[#BE923C]/40 hover:-translate-y-0.5 transition-[transform,background-color,border-color] duration-300"
        >
          <EditableText k="hero.programs" as="span" />
        </button>
      </motion.div>
    </>
  );
}

function CrossfadeImage({ src, alt }) {
  return (
    <div className="relative w-full h-full">
      <AnimatePresence initial={false}>
        <motion.img
          key={src}
          src={src}
          alt={alt || ""}
          initial={{ opacity: 0, scale: 1.06 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1, ease: "easeOut" }}
          className="absolute inset-0 w-full h-full object-cover"
        />
      </AnimatePresence>
    </div>
  );
}

export function Hero() {
  const ref = useRef(null);
  const { t } = useLang();
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => (await api.get("/settings")).data,
  });
  const { data: gallery = [] } = useQuery({
    queryKey: ["gallery"],
    queryFn: async () => (await api.get("/gallery")).data,
  });
  const heroSrc = settings?.hero_image ? resolveImg(settings.hero_image) : DEFAULT_HERO;
  const layout = settings?.hero_layout || "split";
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  // Parallax only on desktop. On mobile the address-bar show/hide changes the
  // viewport height, which makes scroll-linked transforms jump ("glitchy scroll").
  const [isDesktop, setIsDesktop] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px) and (pointer: fine)");
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  const parallaxStyle = isDesktop ? { y: imgY, scale } : undefined;

  // ---- Rotating collage pool: 3 visible slots, images rotate one at a time ----
  const collageConfigured = (settings?.hero_collage_images || []).map(resolveImg).filter(Boolean);
  const galleryImgs = gallery.map((x) => resolveImg(x.image_url)).filter(Boolean);
  const collagePool = (collageConfigured.length ? collageConfigured : [heroSrc, ...galleryImgs]).filter(Boolean);
  const pool = collagePool.length ? collagePool : [heroSrc];
  const poolLen = pool.length;
  const [slots, setSlots] = useState([0, 1, 2]);
  const turnRef = useRef(0);
  const nextRef = useRef(3);
  const pausedRef = useRef(false);
  useEffect(() => {
    if (poolLen <= 3) return;
    if (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const id = setInterval(() => {
      if (pausedRef.current) return;
      setSlots((prev) => {
        const slot = turnRef.current % 3;
        const visible = new Set(prev);
        let candidate = nextRef.current % poolLen;
        let guard = 0;
        while (visible.has(candidate) && guard < poolLen) { candidate = (candidate + 1) % poolLen; guard++; }
        const next = [...prev];
        next[slot] = candidate;
        nextRef.current = candidate + 1;
        turnRef.current += 1;
        return next;
      });
    }, 4000);
    return () => clearInterval(id);
  }, [poolLen]);
  const big = pool[slots[0] % poolLen];
  const s1 = pool[slots[1] % poolLen];
  const s2 = pool[slots[2] % poolLen];

  // ---- Centered layout ----
  if (layout === "centered") {
    return (
      <section ref={ref} id="home" data-testid="hero-section" data-hero-layout="centered" className="relative lg:min-h-screen overflow-hidden pt-24 pb-14 lg:pb-0 bg-[#F9F8F6]">
        <Ambient />
        <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
          <div className="text-center pt-10 sm:pt-16">
            <Copy t={t} centered />
          </div>
          <motion.div
            initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 1 }}
            className="mt-14 relative"
          >
            <div aria-hidden className="absolute -inset-6 bg-gradient-to-tr from-[#BE923C]/22 via-transparent to-[#003063]/12 blur-3xl rounded-[3rem] -z-10" />
            <div className="relative h-[300px] sm:h-[420px] lg:h-[500px] rounded-[2.5rem] overflow-hidden shadow-[0_30px_80px_rgba(15,23,42,0.2)]">
              <motion.img style={parallaxStyle} src={heroSrc} alt={t("hero.imgcap1")} className="absolute inset-0 w-full h-[120%] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/40 via-transparent to-transparent" />
              <div className="absolute inset-0 rounded-[2.5rem] border border-white/25 pointer-events-none" />
            </div>
          </motion.div>
        </div>
      </section>
    );
  }

  // ---- Collage layout ----
  if (layout === "collage") {
    return (
      <section ref={ref} id="home" data-testid="hero-section" data-hero-layout="collage" className="relative lg:min-h-screen overflow-hidden pt-20 pb-14 lg:pb-0 bg-[#F9F8F6]">
        <Ambient />
        <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-8 items-start lg:items-center lg:min-h-[calc(100vh-5rem)]">
          <div className="lg:col-span-6 relative z-10 pt-8 pb-6 lg:py-16">
            <Copy t={t} />
          </div>
          <div className="lg:col-span-6 relative" onMouseEnter={() => { pausedRef.current = true; }} onMouseLeave={() => { pausedRef.current = false; }}>
            <div aria-hidden className="absolute -inset-6 bg-gradient-to-tr from-[#BE923C]/22 via-transparent to-[#003063]/12 blur-3xl rounded-[3rem] -z-10" />
            <div className="grid grid-cols-2 gap-4">
              <motion.div
                initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.9 }}
                className="col-span-2 h-[240px] sm:h-[300px] rounded-[2rem] overflow-hidden shadow-[0_24px_60px_rgba(15,23,42,0.18)]"
              >
                <CrossfadeImage src={big} alt={t("hero.imgcap1")} />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55, duration: 0.9 }}
                className="h-[180px] sm:h-[220px] rounded-[1.5rem] overflow-hidden shadow-[0_20px_50px_rgba(15,23,42,0.15)]"
              >
                <CrossfadeImage src={s1} alt="" />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.9 }}
                className="h-[180px] sm:h-[220px] rounded-[1.5rem] overflow-hidden shadow-[0_20px_50px_rgba(15,23,42,0.15)]"
              >
                <CrossfadeImage src={s2} alt="" />
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // ---- Split layout (default) ----
  return (
    <section ref={ref} id="home" data-testid="hero-section" data-hero-layout="split" className="relative lg:min-h-screen overflow-hidden pt-20 pb-14 lg:pb-0 bg-[#F9F8F6]">
      <Ambient />
      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-8 items-start lg:items-center lg:min-h-[calc(100vh-5rem)]">
        <div className="lg:col-span-7 relative z-10 pt-8 pb-6 lg:py-16">
          <Copy t={t} />
        </div>
        <div className="lg:col-span-5 relative">
          <div aria-hidden className="absolute -inset-5 bg-gradient-to-tr from-[#BE923C]/25 via-transparent to-[#003063]/12 blur-3xl rounded-[3rem] -z-10" />
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.4, duration: 1 }}
            className="relative h-[380px] sm:h-[520px] lg:h-[620px] rounded-[2.5rem] overflow-hidden shadow-[0_30px_80px_rgba(15,23,42,0.2)]"
          >
            <motion.img style={parallaxStyle} src={heroSrc} alt={t("hero.imgcap1")} className="absolute inset-0 w-full h-[115%] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/55 via-transparent to-transparent" />
            <div className="absolute inset-0 rounded-[2.5rem] border border-white/25 pointer-events-none" />
            <div className="absolute bottom-6 left-6 right-6 text-white">
              <p className="font-serif text-2xl">{t("hero.imgcap1")}</p>
              <p className="text-sm text-white/80">{t("hero.imgcap2")}</p>
            </div>
          </motion.div>
        </div>
      </div>
      <ScrollCue t={t} />
    </section>
  );
}
